/**
 * Sidebar navigation registry — role-aware, sectioned.
 *
 * Modelled after the mockup's `renderSidebar()` (mockup §7721-7787):
 *
 *   council:   Today (FA workspace, Decisions)
 *              Demand pipeline (List, Kanban)
 *              Product pipeline (List, Kanban) — disabled "coming soon"
 *              Explore (Talent Directory)
 *              Reference (Help & processes)
 *
 *   talent:    (no heading) Home, Browse opportunities, My profile
 *              Resources (Help)
 *
 * Section headings are pure layout — they have no `to`. Nav items
 * carry a `badge` slot that the Sidebar wires to live counts.
 */

import type { ReactNode } from 'react'
import {
  DocumentAdd24Regular,
  Board24Regular,
  CalendarLtr24Regular,
  Flash24Regular,
  History24Regular,
  Home24Regular,
  Mail24Regular,
  MailClock24Regular,
  DataUsage24Regular,
  Megaphone24Regular,
  People24Regular,
  PeopleTeam24Regular,
  Person24Regular,
  QuestionCircle24Regular,
  Search24Regular,
  Settings24Regular,
  ShieldCheckmark24Regular,
  Sparkle24Regular,
  Target24Regular,
  TextBulletListSquare24Regular,
  TicketDiagonal24Regular,
  ArrowForward24Regular,
  ClipboardTaskListLtr24Regular,
} from '@fluentui/react-icons'

export type NavRole = 'member' | 'talent' | 'council' | 'admin' | 'super_admin'

/** Live data the sidebar passes in so badges can be wired without
 * each entry needing its own hook. */
export interface NavBadgeContext {
  pendingDecisions?: number
  unreadNotifications?: number
}

export type BadgeSource = 'pendingDecisions' | 'unreadNotifications'

/** A clickable nav item. */
export interface NavLinkEntry {
  kind: 'link'
  to: string
  label: string
  icon: ReactNode
  /** Key into NavBadgeContext; sidebar resolves to a number. */
  badgeSource?: BadgeSource
  disabled?: boolean
  disabledTooltip?: string
}

/** A section heading (uppercase muted label). */
export interface NavSectionEntry {
  kind: 'section'
  label: string
}

export type NavEntry = NavLinkEntry | NavSectionEntry

/** Live data that decides which entries exist at all (badges only decorate). */
export interface NavContext {
  /** The Product pipeline section stays hidden until at least one product
   * exists. The entity shipped in v3.0.0 but has been dormant since — two
   * look-alike pipelines in the sidebar, one permanently empty, read as
   * noise and got reported as "can't tell Demand and Product apart"
   * (#1237). Routes stay live for deep links (a promoted demand still
   * links to its product); the first real product brings the section
   * back via the sidebar's presence query. */
  hasProducts?: boolean
  /** Talent surfaces (Browse opportunities) are opt-in, not a birthright of
   *  membership (basic-user split, UAT 2026-09-11). A plain member sees the
   *  basic nav until they activate their talent profile; the legacy 'talent'
   *  ROLE implies the flag — those accounts predate the split. */
  talentProfileActive?: boolean
  /** A DSAI cluster lead names caretakers from the DSAI queue, so a lead of
   *  any role gets the entry. Admins have it anyway (TRIAGE_ENTRIES). */
  leadsDsaiCluster?: boolean
  viewsAllDemands?: boolean
}

const PRODUCT_SECTION_LABEL = 'Product pipeline'

function isProductEntry(entry: NavEntry): boolean {
  return entry.kind === 'section'
    ? entry.label === PRODUCT_SECTION_LABEL
    : entry.to.startsWith('/products')
}

/* ─── Council nav ────────────────────────────────────────────────────── */
const COUNCIL_ENTRIES: NavEntry[] = [
  { kind: 'section', label: 'Today' },
  // Home → FA Workspace. Sidebar deep-links to /focus-areas/{code} if user has a home FA.
  { kind: 'link', to: '/focus-areas', label: 'Home', icon: <Target24Regular /> },
  // Hidden until triage UX is validated — route still exists for direct access
  // {
  //   kind: 'link',
  //   to: '/triage',
  //   label: 'Decisions',
  //   icon: <Mail24Regular />,
  //   badgeSource: 'pendingDecisions',
  // },

  { kind: 'section', label: 'Demand pipeline' },
  // No Audit queue or Sent to DSAI here (#1630): triage is the classifier's
  // job now. The home strip links the Audit queue while arrivals wait, a
  // routed demand's banner links its DSAI entry, and cluster leads and
  // admins get them below (DSAI_QUEUE_ENTRY, TRIAGE_ENTRIES).
  { kind: 'link', to: '/demands', label: 'List view', icon: <TextBulletListSquare24Regular /> },
  { kind: 'link', to: '/demands/kanban', label: 'Kanban view', icon: <Board24Regular /> },

  // Product pipeline — peer to Demand pipeline (v3.0.0). List + Kanban live.
  { kind: 'section', label: 'Product pipeline' },
  { kind: 'link', to: '/products', label: 'List view', icon: <TextBulletListSquare24Regular /> },
  { kind: 'link', to: '/products/kanban', label: 'Kanban view', icon: <Board24Regular /> },

  { kind: 'section', label: 'Explore' },
  { kind: 'link', to: '/talents', label: 'Talent Directory', icon: <People24Regular /> },
  // No Reports entry until the page exists: a disabled "Coming soon" link is
  // a dead end in the nav. The /reports route and placeholder page remain.

  { kind: 'section', label: 'Reference' },
  {
    kind: 'link',
    to: '/my-submissions',
    label: 'My submissions',
    icon: <TextBulletListSquare24Regular />,
  },
  { kind: 'link', to: '/tickets', label: 'Tickets', icon: <TicketDiagonal24Regular /> },
  { kind: 'link', to: '/settings', label: 'Settings', icon: <Settings24Regular /> },
  { kind: 'link', to: '/whats-new', label: "What's new", icon: <Sparkle24Regular /> },
  { kind: 'link', to: '/help', label: 'Help & processes', icon: <QuestionCircle24Regular /> },
]

/* ─── Admin-only nav extension ───────────────────────────────────────── */
// Appended to COUNCIL_ENTRIES when the effective role is admin / super_admin.
// Lives separately so the section is only built for users who can see it,
// and to keep the council nav unchanged.
const ADMIN_ENTRIES: NavEntry[] = [
  { kind: 'section', label: 'Admin' },
  { kind: 'link', to: '/admin/users', label: 'All users', icon: <PeopleTeam24Regular /> },
  { kind: 'link', to: '/admin/roster-health', label: 'Roster health', icon: <ShieldCheckmark24Regular /> },
  { kind: 'link', to: '/admin/audit-log', label: 'Audit log', icon: <History24Regular /> },
  { kind: 'link', to: '/admin/activity', label: 'Activity stream', icon: <Flash24Regular /> },
  { kind: 'link', to: '/admin/email-log', label: 'Email log', icon: <Mail24Regular /> },
  { kind: 'link', to: '/admin/posting-results', label: 'Posting results', icon: <DataUsage24Regular /> },
  // Read-only: what the app sends and why. Sits next to the log so the two
  // halves of the question — "what went out" and "what would go out" — are
  // in the same place.
  { kind: 'link', to: '/admin/notifications', label: 'Notifications', icon: <Mail24Regular /> },
  // Who gets the coming Monday mail, and what it says (#1500).
  { kind: 'link', to: '/admin/monday-mail', label: 'Monday mail', icon: <MailClock24Regular /> },
  { kind: 'link', to: '/admin/announcements', label: 'Announcements', icon: <Megaphone24Regular /> },
  // The cycle planning meeting (#1522): the plan for the developers and the
  // people planning with them.
  { kind: 'link', to: '/admin/planning', label: 'Planning', icon: <CalendarLtr24Regular /> },
]

/* ─── Talent nav ─────────────────────────────────────────────────────── */
const TALENT_ENTRIES: NavEntry[] = [
  {
    kind: 'link',
    to: '/',
    label: 'Home',
    icon: <Home24Regular />,
    // unreadNotifications badge moved to the header bell so all roles see it
    // in the same spot; nav entry is now badge-free to avoid duplication.
  },
  // The one action every user has (UAT 2026-09-11: "容易找不着" — the page
  // existed only as the external front door, with no way in from the app).
  { kind: 'link', to: '/submit-demand', label: 'Submit a demand', icon: <DocumentAdd24Regular /> },
  { kind: 'link', to: '/opportunities', label: 'Browse opportunities', icon: <Search24Regular /> },
  {
    kind: 'link',
    to: '/my-submissions',
    label: 'My submissions',
    icon: <TextBulletListSquare24Regular />,
  },
  { kind: 'link', to: '/profile', label: 'My profile', icon: <Person24Regular /> },

  { kind: 'section', label: 'Resources' },
  { kind: 'link', to: '/tickets', label: 'Tickets', icon: <TicketDiagonal24Regular /> },
  // No Settings entry here: for a talent or member it opened the same page as
  // "My profile" above, under a second name (#1378). Council keeps theirs.
  { kind: 'link', to: '/whats-new', label: "What's new", icon: <Sparkle24Regular /> },
  { kind: 'link', to: '/help', label: 'Help', icon: <QuestionCircle24Regular /> },
]

/** What DSAI sees of the requests we pass them. Only cluster leads have it in
 *  the sidebar: it is where they set status and name a caretaker (#1610). */
const DSAI_QUEUE_ENTRY: NavLinkEntry = {
  kind: 'link',
  to: '/dsai',
  label: 'Sent to DSAI',
  icon: <ArrowForward24Regular />,
}

/** Admins keep both triage pages: they check the classifier's work. */
const TRIAGE_ENTRIES: NavLinkEntry[] = [
  { kind: 'link', to: '/audit-queue', label: 'Audit queue', icon: <ClipboardTaskListLtr24Regular /> },
  DSAI_QUEUE_ENTRY,
]

/** `entries` with `added` right after the link to `after`. */
function insertAfter(entries: NavEntry[], after: string, added: NavEntry[]): NavEntry[] {
  const at = entries.findIndex((e) => e.kind === 'link' && e.to === after)
  return [...entries.slice(0, at + 1), ...added, ...entries.slice(at + 1)]
}

/**
 * Returns the visible nav for a given role.
 * - talent → talent nav
 * - council → council nav
 * - admin / super_admin → council nav + admin section
 * - unauthenticated → empty
 *
 * `ctx` carries the data-dependent visibility decisions (see NavContext);
 * omitting it is the conservative default — data-gated sections stay hidden.
 */
export function visibleEntriesForRole(
  role: NavRole | undefined | null,
  ctx: NavContext = {},
): NavEntry[] {
  if (!role) return []
  if (role === 'talent' || role === 'member') {
    const active = role === 'talent' || !!ctx.talentProfileActive
    // The basic nav is the talent nav minus the talent-only surface — derived,
    // not a second list, so a new entry cannot be added to one and forgotten
    // in the other.
    const base = active
      ? TALENT_ENTRIES
      : TALENT_ENTRIES.filter((e) => !(e.kind === 'link' && e.to === '/opportunities'))
    const personal = ctx.leadsDsaiCluster
      ? insertAfter(base, '/my-submissions', [DSAI_QUEUE_ENTRY])
      : base
    if (!ctx.viewsAllDemands) return personal
    return [
      ...COUNCIL_ENTRIES.filter((e) => e.kind === 'link' &&
        ['/focus-areas', '/demands', '/demands/kanban'].includes(e.to)),
      ...personal.filter((e) => !(e.kind === 'link' && e.to === '/')),
    ]
  }
  const isAdmin = role === 'admin' || role === 'super_admin'
  const triage = isAdmin ? TRIAGE_ENTRIES : ctx.leadsDsaiCluster ? [DSAI_QUEUE_ENTRY] : []
  const council = insertAfter(COUNCIL_ENTRIES, '/demands/kanban', triage)
  const entries = isAdmin ? [...council, ...ADMIN_ENTRIES] : council
  return ctx.hasProducts ? entries : entries.filter((e) => !isProductEntry(e))
}
